import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://212.47.74.155:9000',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Log para depuração (remover em produção se necessário)
      if (process.env.NODE_ENV === 'development') {
        console.log(`📤 Enviando requisição para: ${config.method?.toUpperCase()} ${config.url}`);
        console.log(`🔑 Token presente: ${token.substring(0, 20)}...`);
      }
    } else {
      console.warn('⚠️ Token não encontrado no localStorage');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de resposta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token inválido ou expirado
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Só redirecionar se não estiver já na página de login
      if (window.location.pathname !== '/login') {
        // Usar replace para evitar voltar para a página anterior
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Serviços de autenticação
export const authService = {
  login: async (username, password) => {
    const response = await api.post('/api/auth/login', { username, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  
  verifyOTP: async (tempToken, otpCode) => {
    const response = await api.post('/api/auth/verify-otp', { tempToken, otpCode });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  
  setupOTP: async (tempToken) => {
    const response = await api.post('/api/auth/setup-otp', { tempToken });
    return response.data;
  },
  
  enableOtp: async (tempToken, otpCode) => {
    const response = await api.post('/api/auth/enable-otp', { tempToken, otpCode });
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },
  
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
  
  getMe: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  }
};

// Serviços de notificações
export const notificacoesService = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/notificacoes?${params}`);
    return response.data;
  },
  
  markAsRead: async (id) => {
    const response = await api.put(`/api/notificacoes/${id}/lida`);
    return response.data;
  },
  
  markAllAsRead: async () => {
    const response = await api.put('/api/notificacoes/lidas');
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/notificacoes/${id}`);
    return response.data;
  }
};

// Serviços de vendas
export const salesService = {
  getAll: async (filters = {}, page = 1, limit = 20) => {
    const paramsObj = { ...filters, page, limit };
    const params = new URLSearchParams(paramsObj).toString();
    const response = await api.get(`/api/sales?${params}`);
    return response.data;
  },
  
  getAllForStats: async (filters = {}) => {
    // Buscar todas as vendas sem paginação para estatísticas
    // Usar um limite alto para pegar todas as vendas
    const paramsObj = { ...filters, page: 1, limit: 100000 };
    const params = new URLSearchParams(paramsObj).toString();
    const response = await api.get(`/api/sales?${params}`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/sales/${id}`);
    return response.data;
  },

  registrarVisualizacao: async (id, source) => {
    const response = await api.post(`/api/sales/${id}/registrar-visualizacao`, { source });
    return response.data;
  },

  getHistoricoVisualizacao: async (id) => {
    const response = await api.get(`/api/sales/${id}/historico-visualizacao`);
    return response.data;
  },
  
  create: async (saleData, isFormData = false) => {
    const config = isFormData 
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    const response = await api.post('/api/sales', saleData, config);
    return response.data;
  },
  
  update: async (id, saleData) => {
    const response = await api.put(`/api/sales/${id}`, saleData);
    return response.data;
  },
  
  updateStatus: async (id, status, observacao_tratamento, data_instalacao) => {
    const body = { status, observacao_tratamento };
    if (data_instalacao) body.data_instalacao = data_instalacao;
    const response = await api.put(`/api/sales/${id}/status`, body);
    return response.data;
  },
  
  updateAtribuicaoBko: async (id, tratado_por) => {
    const response = await api.put(`/api/sales/${id}/atribuicao-bko`, { 
      tratado_por 
    });
    return response.data;
  },
  
  updateAtribuicaoVendedor: async (id, vendedor_id) => {
    const response = await api.put(`/api/sales/${id}/atribuicao-vendedor`, { 
      vendedor_id 
    });
    return response.data;
  },
  
  getQueue: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/sales/queue/list?${params}`);
    return response.data;
  },
  
  getStats: async (mes, ano) => {
    const params = new URLSearchParams();
    if (mes != null && mes >= 1 && mes <= 12) params.set('mes', String(mes));
    if (ano != null && ano >= 2000) params.set('ano', String(ano));
    const query = params.toString();
    const response = await api.get(`/api/sales/stats/dashboard${query ? `?${query}` : ''}`);
    return response.data;
  },
  
  buscarCnpj: async (cnpj) => {
    const response = await api.get(`/api/sales/utils/buscar-cnpj/${cnpj}`);
    return response.data;
  },
  
  // Vendas concluídas para controle de pagamento (paginação, busca CPF/CNPJ/nome, filtro operadora)
  getVendasConcluidas: async (params = {}) => {
    const q = new URLSearchParams();
    if (params.status_pagamento) q.set('status_pagamento', params.status_pagamento);
    if (params.page) q.set('page', params.page);
    if (params.limit) q.set('limit', params.limit);
    if (params.busca && params.busca.trim()) q.set('busca', params.busca.trim());
    if (params.operadora_id) q.set('operadora_id', params.operadora_id);
    if (params.vendedor_id) q.set('vendedor_id', params.vendedor_id);
    const query = q.toString();
    const response = await api.get(`/api/sales/pagamento/concluidas${query ? `?${query}` : ''}`);
    return response.data;
  },
  
  updateStatusPagamento: async (id, status_pagamento, data_pagamento) => {
    const response = await api.put(`/api/sales/${id}/pagamento`, {
      status_pagamento,
      data_pagamento
    });
    return response.data;
  },

  getResumoPagamentoPorOperadora: async (operadoraId) => {
    const response = await api.get(`/api/sales/pagamento/resumo-por-operadora?operadora_id=${operadoraId}`);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/sales/${id}`);
    return response.data;
  },
  
  exportSales: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/sales/export?${params}`, {
      responseType: 'blob'
    });
    
    // Criar link de download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extrair nome do arquivo do header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'vendas.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

// Serviços de usuários
export const usersService = {
  getAll: async () => {
    const response = await api.get('/api/users');
    return response.data;
  },
  
  create: async (userData) => {
    const response = await api.post('/api/users', userData);
    return response.data;
  },
  
  update: async (id, userData) => {
    const response = await api.put(`/api/users/${id}`, userData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/users/${id}`);
    return response.data;
  },
  
  getVendedoresBySupervisor: async (supervisorId) => {
    const response = await api.get(`/api/users/supervisor/${supervisorId}/vendedores`);
    return response.data;
  },
  
  getOperadoras: async (userId) => {
    const response = await api.get(`/api/users/${userId}/operadoras`);
    return response.data;
  },
  
  updateSupervisoresSubordinados: async (coordenadorId, supervisoresIds) => {
    const response = await api.put(`/api/users/${coordenadorId}/supervisores-subordinados`, {
      supervisores_subordinados: Array.isArray(supervisoresIds) ? supervisoresIds : []
    });
    return response.data;
  },

  updateOperadoras: async (userId, operadoras, atender_todas) => {
    const response = await api.put(`/api/users/${userId}/operadoras`, {
      operadoras,
      atender_todas
    });
    return response.data;
  },
  
  // Consultas de viabilidade
  getViabilidadeConsultas: async (userId, limit = 100, mes, ano) => {
    const params = new URLSearchParams({ limit, mes, ano }).toString();
    const response = await api.get(`/api/users/${userId}/consultas-viabilidade?${params}`);
    return response.data;
  },
  
  getViabilidadeResumo: async (userId, mes, ano) => {
    const response = await api.get(`/api/users/${userId}/consultas-viabilidade/resumo?mes=${mes}&ano=${ano}`);
    return response.data;
  },
  
  // Inatividade
  getInatividadeStatus: async () => {
    const response = await api.get('/api/users/inatividade/status');
    return response.data;
  },
  
  inativarUsuarios: async (userIds) => {
    const response = await api.post('/api/users/inatividade/inativar', { userIds });
    return response.data;
  },
  
  resetOtp: async (userId) => {
    const response = await api.post(`/api/users/${userId}/reset-otp`);
    return response.data;
  },
  
  reativarUsuario: async (userId) => {
    const response = await api.put(`/api/users/${userId}/reativacao`);
    return response.data;
  },
  
  getOtpStatus: async (userId) => {
    const response = await api.get(`/api/users/${userId}/otp-status`);
    return response.data;
  }
};

// Serviço de operadoras
export const operadorasService = {
  getAll: async () => {
    const response = await api.get('/api/operadoras');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/operadoras/${id}`);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/api/operadoras/${id}`, data);
    return response.data;
  }
};

// Serviço de busca de CEP
export const viaCepService = {
  buscarCep: async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) {
      throw new Error('CEP inválido');
    }
    
    const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
    
    if (response.data.erro) {
      throw new Error('CEP não encontrado');
    }
    
    return response.data;
  }
};

// Serviço de consulta de viabilidade
export const viabilidadeService = {
  consultar: async (cep, numero) => {
    const response = await api.post('/api/viabilidade/consultar', {
      cep,
      numero
    }, {
      timeout: 120000 // 2 minutos de timeout
    });
    return response.data;
  }
};

// Serviço de configuração da API
export const apiConfigService = {
  getCredentials: async () => {
    const response = await api.get('/api/api-config/credentials');
    return response.data;
  },
  
  createCredential: async (username, password) => {
    const response = await api.post('/api/api-config/credentials', {
      username,
      password
    });
    return response.data;
  },
  
  updateCredential: async (id, data) => {
    const response = await api.put(`/api/api-config/credentials/${id}`, data);
    return response.data;
  },
  
  deleteCredential: async (id) => {
    const response = await api.delete(`/api/api-config/credentials/${id}`);
    return response.data;
  },
  
  testConnection: async (username, password) => {
    const response = await api.post('/api/api-config/test', {
      username,
      password
    });
    return response.data;
  },
  
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    
    const response = await api.post('/api/api-config/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  
  getLogoUrl: () => {
    const baseURL = process.env.REACT_APP_API_URL || 'http://212.47.74.155:9000';
    return `${baseURL}/api/api-config/logo`;
  },

  // Token API DataNexus (consulta CPF)
  getCpfToken: async () => {
    const response = await api.get('/api/api-config/cpf-token');
    return response.data;
  },

  updateCpfToken: async (token) => {
    const response = await api.put('/api/api-config/cpf-token', { token });
    return response.data;
  },

  buscarCpf: async (cpf) => {
    const cpfLimpo = String(cpf).replace(/\D/g, '');
    const response = await api.get(`/api/api-config/buscar-cpf/${cpfLimpo}`);
    return response.data;
  }
};

// Serviço de anúncios
const getBaseUrl = () => process.env.REACT_APP_API_URL || 'http://212.47.74.155:9000';

export const announcementsService = {
  create: async (data) => {
    const formData = new FormData();
    formData.append('titulo', data.titulo);
    formData.append('conteudo', data.conteudo);
    formData.append('data_inicio', data.data_inicio);
    formData.append('data_fim', data.data_fim);
    data.target_roles.forEach(role => formData.append('target_roles[]', role));
    if (data.imagem && data.imagem instanceof File) {
      formData.append('imagem', data.imagem);
    }
    const response = await api.post('/api/announcements', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getVisible: async () => {
    const response = await api.get('/api/announcements');
    return response.data;
  },

  getAll: async () => {
    const response = await api.get('/api/announcements?manage=1');
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/api/announcements/${id}`);
  },

  getImageUrl: (imagemPath) => {
    if (!imagemPath) return null;
    const base = getBaseUrl().replace(/\/$/, '');
    const path = imagemPath.startsWith('/') ? imagemPath : `/${imagemPath}`;
    return `${base}${path}`;
  }
};

// Serviço catálogo de planos
export const catalogoPlanosService = {
  list: async (manage = false) => {
    const params = manage ? { manage: '1' } : {};
    const response = await api.get('/api/catalogo-planos', { params });
    return response.data;
  },

  create: async (data) => {
    const formData = new FormData();
    formData.append('titulo', data.titulo);
    if (data.descricao != null) formData.append('descricao', data.descricao);
    if (data.ordem != null) formData.append('ordem', data.ordem);
    if (data.ativo != null) formData.append('ativo', data.ativo);
    if (data.imagem && data.imagem instanceof File) formData.append('imagem', data.imagem);
    const response = await api.post('/api/catalogo-planos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  update: async (id, data) => {
    const formData = new FormData();
    if (data.titulo != null) formData.append('titulo', data.titulo);
    if (data.descricao != null) formData.append('descricao', data.descricao);
    if (data.ordem != null) formData.append('ordem', data.ordem);
    if (data.ativo != null) formData.append('ativo', data.ativo);
    if (data.imagem && data.imagem instanceof File) formData.append('imagem', data.imagem);
    const response = await api.put(`/api/catalogo-planos/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/api/catalogo-planos/${id}`);
  },

  getImageUrl: (imagemPath) => {
    if (!imagemPath) return null;
    const base = getBaseUrl().replace(/\/$/, '');
    const path = imagemPath.startsWith('/') ? imagemPath : `/${imagemPath}`;
    return `${base}${path}`;
  }
};


export default api;
